import React, {useEffect, useState} from "react";
import {Modal, notification, Table, Tag} from "antd";
import {ClockCircleOutlined} from "@ant-design/icons";
import Spinner from "../../spinner";
import {billingServices} from "../../../services/billing.services";
import i18n from "../../../i18n";

// Метки типов действий с подпиской
const ACTION_LABELS = {
    created: {label: 'Создание подписки', color: 'blue'},
    updated: {label: 'Изменение подписки', color: 'gold'},
    invoice_created: {label: 'Выставление счёта', color: 'cyan'},
    charge_failed: {label: 'Попытка списания', color: 'red'},
    charged: {label: 'Списание с депозита', color: 'green'},
};

// Человекочитаемые имена полей подписки для диффа
const FIELD_LABELS = {
    type: 'Тип',
    tariffId: 'Тариф',
    price: 'Цена',
    customPrice: 'Спец. цена',
    legalEntityName: 'Юр. лицо',
    dateEnd: 'Дата окончания',
};

export default function ModalSubscriptionHistory({subscriptionId, isShow, setIsShow}) {
    const [history, setHistory] = useState([]);
    const [loading, setLoading] = useState(false);

    function getPayload(response) {
        if (response && response.data && response.data.data) {
            return response.data.data;
        }
        return [];
    }

    useEffect(() => {
        if (subscriptionId && isShow) {
            setLoading(true);
            billingServices.getSubscriptionHistory(subscriptionId)
                .then((response) => {
                    setHistory(getPayload(response) || []);
                })
                .catch((err) => {
                    const description = typeof err === 'string'
                        ? err
                        : (err && err.message ? err.message : err);
                    notification.error({
                        message: i18n.t('components.notification.titles.subscription'),
                        description: description,
                    });
                })
                .finally(() => setLoading(false));
        }
    }, [subscriptionId, isShow]);

    function formatAmount(value) {
        if (value === null || value === undefined) {
            return '';
        }
        return Number(value).toLocaleString('ru-RU', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        }) + ' ₽';
    }

    function formatActionType(type) {
        const meta = ACTION_LABELS[type];
        if (!meta) {
            return <Tag>{type}</Tag>;
        }
        return <Tag color={meta.color}>{meta.label}</Tag>;
    }

    // Рендер подробностей действия (поле data) в зависимости от типа
    function formatDetails(record) {
        const data = record.data;
        if (!data) {
            return '—';
        }

        switch (record.actionType) {
            case 'created':
                return (
                    <div>
                        {data.tariffName ? <div>Тариф: {data.tariffName}</div> : null}
                        <div>Цена: {formatAmount(data.price)}</div>
                        {data.customPrice !== null && data.customPrice !== undefined
                            ? <div>Спец. цена: {formatAmount(data.customPrice)}</div>
                            : null}
                        {data.dateStart ? <div>Период: {data.dateStart}{data.dateEnd ? ' — ' + data.dateEnd : ''}</div> : null}
                    </div>
                );

            case 'updated': {
                const changes = data.changes;
                if (!changes) {
                    return '—';
                }
                const rows = Object.keys(changes).map((field) => {
                    const label = FIELD_LABELS[field] ? FIELD_LABELS[field] : field;
                    const oldValue = changes[field].old;
                    const newValue = changes[field].new;
                    return (
                        <div key={field}>
                            {label}: <b>{formatFieldValue(field, oldValue)}</b> → <b>{formatFieldValue(field, newValue)}</b>
                        </div>
                    );
                });
                return <div>{data.tariffName ? <div>Тариф: {data.tariffName}</div> : null}{rows}</div>;
            }

            case 'invoice_created':
                return (
                    <div>
                        <div>Счёт №{data.invoiceNumber}</div>
                        <div>Сумма: {formatAmount(data.amount)}</div>
                    </div>
                );

            case 'charged':
                return (
                    <div>
                        <div>Списано: {formatAmount(data.amount)}</div>
                        {data.newDateEnd ? <div>Продлено до: {data.newDateEnd}</div> : null}
                    </div>
                );

            case 'charge_failed':
                return (
                    <div>
                        <div>Сумма: {formatAmount(data.amount)}</div>
                        {data.reason ? <div>Причина: {data.reason}</div> : null}
                    </div>
                );

            default:
                return JSON.stringify(data);
        }
    }

    function formatFieldValue(field, value) {
        if (value === null || value === undefined) {
            return '—';
        }
        if (field === 'price' || field === 'customPrice') {
            return formatAmount(value);
        }
        return String(value);
    }

    const columns = [
        {
            title: 'Дата',
            dataIndex: 'createdAt',
            key: 'createdAt',
        },
        {
            title: 'Тип',
            key: 'actionType',
            render: (_, record) => formatActionType(record.actionType),
        },
        {
            title: 'Подробности',
            key: 'details',
            render: (_, record) => formatDetails(record),
        },
        {
            title: 'Пользователь',
            key: 'user',
            render: (_, record) => {
                if (record.userFio) {
                    return record.userFio;
                }
                if (record.userId) {
                    return 'Пользователь №' + record.userId;
                }
                return '—';
            },
        },
    ];

    return (
        <Modal
            title={<span><ClockCircleOutlined/> История подписки</span>}
            cancelText={i18n.t('main.buttons.close')}
            className="modal-window"
            align={null}
            visible={isShow}
            onCancel={() => setIsShow(false)}
            footer={null}
        >
            <Spinner loading={loading} tip={i18n.t('main.loadText.loading')}>
                <Table
                    columns={columns}
                    dataSource={history}
                    bordered={true}
                    pagination={false}
                    locale={{emptyText: i18n.t('main.empty')}}
                    className="employees-table"
                    rowKey="id"
                />
            </Spinner>
        </Modal>
    );
}
